"use client";

import { IconChainLink4 } from "central-icons/IconChainLink4";
import type { ReactNode } from "react";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { Heading } from "@/components/ui/heading";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import CustomCheckmarkIcon from "@/icons/custom-checkmark-icon";
import { cn } from "@/lib/utils";

interface HeadingProps {
  children: ReactNode;
  id: string;
}

export function H1({
  children,
  className,
  id,
}: HeadingProps & { className?: string }) {
  return (
    <Heading
      as="h1"
      className={cn(
        "mb-5 w-fit scroll-mt-20 text-balance font-[550] text-gray-1200",
        className
      )}
      id={id}
      size={3}
    >
      {children}
    </Heading>
  );
}

function AnchorHeading({
  as: Tag,
  children,
  id,
}: HeadingProps & { as: "h2" | "h3" }) {
  const { isCopied, copy } = useCopyToClipboard();

  return (
    <Heading
      as={Tag}
      className={cn(
        "mb-5 flex scroll-mt-20 items-center gap-3 text-balance font-[550] text-gray-1200",
        Tag === "h2" ? "mt-20" : "mt-10"
      )}
      id={id}
    >
      <a
        className="group relative -ml-7 pl-7 text-left before:absolute before:-inset-1 before:content-['']"
        href={`#${id}`}
        onClick={() =>
          copy(`${window.location.origin}${window.location.pathname}#${id}`)
        }
      >
        <span className="absolute top-1/2 left-0 flex size-6 -translate-y-1/2 items-center justify-center opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100">
          <AnimatedIcon
            active={isCopied}
            activeIcon={
              <CustomCheckmarkIcon className="size-4 text-gray-1000 will-change-transform" />
            }
            idleIcon={
              <IconChainLink4 className="mb-px size-4 text-gray-1000 will-change-transform" />
            }
          />
        </span>
        {children}
      </a>
      {Tag === "h2" && (
        <span aria-hidden="true" className="h-px flex-1 bg-gray-400" />
      )}
    </Heading>
  );
}

export function H2(props: HeadingProps) {
  return <AnchorHeading as="h2" {...props} />;
}

export function H3(props: HeadingProps) {
  return <AnchorHeading as="h3" {...props} />;
}
